import {
  adminMerchantUrl,
  amplitudeUserUrl,
  cognitoUserUrl,
  sentryIssuesForBusinessUrl,
  sentryIssuesForUserUrl,
} from "@/lib/debug-links";
import { connectedAccountUrl } from "@/lib/stripe-links";

import type {
  AuthState,
  DeviceState,
} from "@/application/merchants/debug_context_repository";

export interface DebugBundleInput {
  businessId: string;
  businessName: string | null;
  primaryUserId: string | null;
  stripeConnectedAccountId: string | null;
  lifecycleStage: string | null;
  environment: string;
  deviceState: DeviceState | null;
  authState: AuthState | null;
}

function line(label: string, value: string | null | undefined): string | null {
  return value ? `${label}: ${value}` : null;
}

/**
 * Formats everything needed to hand a merchant's situation to someone else in
 * one paste — the identifiers, the current build, and every deep link.
 *
 * Absent values are omitted rather than rendered as "none", so the reader can
 * tell "we don't have this" from "this is empty" at a glance.
 */
export function formatDebugBundle(input: DebugBundleInput): string {
  const device = input.deviceState;
  const cognito = input.primaryUserId
    ? cognitoUserUrl(input.primaryUserId)
    : null;

  const rows = [
    line("Business", input.businessName),
    line("Business ID", input.businessId),
    line("User (sub)", input.primaryUserId),
    line("Email", input.authState?.email),
    line("Stripe account", input.stripeConnectedAccountId),
    line("Lifecycle", input.lifecycleStage),
    line("Environment", input.environment),
    device?.appVersion
      ? `App: ${device.appVersion}${device.buildNumber ? ` (${device.buildNumber})` : ""}`
      : null,
    device?.deviceModel || device?.osName
      ? `Device: ${[device.deviceModel, device.osName, device.osVersion]
          .filter(Boolean)
          .join(" / ")}`
      : null,
    line("Last seen", device?.lastSeenAt),
    line("Cognito status", input.authState?.status),
    "",
    line("Admin", adminMerchantUrl(input.businessId)),
    input.primaryUserId
      ? line("Amplitude", amplitudeUserUrl(input.primaryUserId))
      : null,
    line(
      "Sentry",
      input.primaryUserId
        ? sentryIssuesForUserUrl(input.primaryUserId)
        : sentryIssuesForBusinessUrl(input.businessId),
    ),
    line("Cognito", cognito),
    input.stripeConnectedAccountId
      ? line("Stripe", connectedAccountUrl(input.stripeConnectedAccountId))
      : null,
  ];

  return rows
    .filter((r) => r !== null)
    .join("\n")
    .trim();
}
